import React, { useState, useEffect } from 'react';
import { createSource, updateSource } from '../../services/sourceService';
import { ICreateSourceRequest, IUpdateSourceRequest, SourceItem } from '../../../../models';
import SourceFormFields, { SourceFormData } from './SourceFormFields';
import { Dialog, Button, Card, CardContent } from '../ui';

interface SourceFormProps {
  // Common props
  onSave: () => void;
  initialData?: SourceItem;

  // Modal dialog props (for create mode)
  open?: boolean;
  onClose?: () => void;

  // Inline form props (for edit mode)
  isEdit?: boolean;
  onCancel?: () => void;
}

const SourceForm: React.FC<SourceFormProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  isEdit = false,
  onCancel
}) => {
  const [formData, setFormData] = useState<SourceFormData>({
    name: '',
    sourceType: 'URL_LIST',
    sourceUrl: '',
    searchResultsConfig: undefined,
    urlFilterPattern: '',
    keepQueryParams: '',
    description: '',
    scrapingSchedule: '',
    agencyId: '',
    domain: '',
    filters: [],
    notificationEmails: [],
    js: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        sourceType: initialData.sourceType || 'URL_LIST',
        sourceUrl: initialData.sourceUrl || '',
        searchResultsConfig: initialData.searchResultsConfig
          ? {
              ...initialData.searchResultsConfig,
              useBrowser:
                initialData.searchResultsConfig.useBrowser ?? false,
            }
          : undefined,
        urlFilterPattern: initialData.urlFilterPattern || '',
        keepQueryParams: initialData.keepQueryParams || '',
        description: initialData.description || '',
        scrapingSchedule: initialData.scrapingSchedule || '',
        agencyId: initialData.agencyId || '',
        domain: initialData.domain || '',
        filters: initialData.filters || [],
        notificationEmails: initialData.notificationEmails || [],
        js: initialData.js || [],
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;

    // Handle sourceType changes - clear related fields when switching types
    if (name === 'sourceType') {
      setFormData({
        ...formData,
        sourceType: value as any,
        sourceUrl: '',
        searchResultsConfig:
          value === 'SEARCH_RESULTS'
            ? {
                propertyLinkSelector: '',
                nextPageSelector: '',
                maxPages: 10,
                useBrowser: false,
              }
            : undefined,
        urlFilterPattern: '',
      });
    } else {
      setFormData({
        ...formData,
        [name as string]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (isEdit && initialData) {
        await updateSource(initialData.id, formData as IUpdateSourceRequest);
      } else {
        await createSource(formData as ICreateSourceRequest);
      }

      onSave();

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} source:`, error);
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <SourceFormFields
      formData={formData}
      onChange={handleChange}
      isEdit={isEdit}
    />
  );

  // For modal dialog (create mode)
  if (!isEdit) {
    return (
      <Dialog
        open={open || false}
        onClose={onClose || (() => {})}
        title="Add New Source"
        maxWidth="lg"
        footer={
          <>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="mt-1">
            {formContent}
          </div>
        </form>
      </Dialog>
    );
  }

  // For inline form (edit mode)
  return (
    <Card className="shadow-md">
      <CardContent>
        <form onSubmit={handleSubmit}>
          {formContent}

          <div className="mt-6 flex justify-end gap-2">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Save'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default SourceForm;
